"use client";

import type { HeaderNavAction, HeaderNavCard, HeaderNavGroup, HeaderNavigation } from "@lib/types";

import { SITE_NAME } from "@/constants";

type LatestArticleEntry = {
  _id: string;
  _type: string;
  title?: string;
  slug?: string;
  summary?: string;
  publishedAt?: string;
  image?: { image?: unknown; alt?: string } | null;
};

type RawNavAside = NonNullable<HeaderNavGroup["aside"]> & {
  autoPopulateLatest?: boolean | null;
  latestArticleTypes?: string[] | null;
  latestArticles?: LatestArticleEntry[] | null;
};

type SanitizedNavCard = HeaderNavCard & { _key: string };
type SanitizedNavAside = {
  variant?: "cards" | "list" | "none" | null;
  cards?: SanitizedNavCard[];
  linkList?: {
    title: string;
    links: Array<{
      _key: string;
      title: string;
      subtitle?: string;
      href?: string;
    }>;
  } | null;
};
type SanitizedNavGroup = Omit<HeaderNavGroup, "aside"> & {
  aside?: SanitizedNavAside | null;
};

import { stegaClean } from "@sanity/client/stega";
import { AnimatePresence, motion, type Variants } from "framer-motion";
import Image from "next/image";
import Link from "next/link";
import { usePathname } from "next/navigation";
import { startTransition, useEffect, useMemo, useState } from "react";

import { capitalizeFirst, formatDate, getArticleUrl } from "@/components/article";
import { resolveCtaHref } from "@/components/shared/cta";
import PortableText, { PortableTextValue } from "@/components/shared/portable-text";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { Button } from "@/components/ui/button";
import { Container } from "@/components/ui/container";
import { icon, icons } from "@/components/ui/icons";
import { Separator } from "@/components/ui/separator";
import { TextAnimate } from "@/components/ui/text-animate";
import { stripStega } from "@/lib/sanity/stega";
import { cn } from "@/lib/ui/cn";
import { urlForImageCached } from "@/sanity/lib/utils";
import { resolveReference } from "@/sanity/utils/reference";

import { HeaderActionButton } from "./HeaderActions";

const CloseIcon = icons.X;

// Open/close animation behavior
const DURATION = 0.3;
const EASE = "easeInOut";
const parentVariants: Variants = {
  closed: {
    transition: {
      // on close, animate panel first, overlay last
      staggerChildren: 0.08,
      staggerDirection: -1,
    },
  },
  open: {
    transition: {
      // on open, animate overlay first, panel after
      delayChildren: 0,
      staggerChildren: 0.08,
    },
  },
};
const overlayVariants: Variants = {
  closed: {
    opacity: 0,
    backdropFilter: "blur(0px)",
    transition: { duration: DURATION, ease: EASE },
  },
  open: {
    opacity: 1,
    backdropFilter: "blur(12px)",
    transition: { duration: DURATION, ease: EASE },
  },
};
const panelVariants: Variants = {
  closed: {
    x: -40,
    opacity: 0,
    transition: { duration: DURATION, ease: EASE },
  },
  open: {
    x: 0,
    opacity: 1,
    transition: { duration: DURATION, ease: EASE },
  },
};

type HeaderMenuProps = {
  navigation: HeaderNavigation | null;
  primaryAction?: HeaderNavAction | null;
  secondaryAction?: HeaderNavAction | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  logoSrc?: string | null;
  logoAlt?: string;
  isLogoSvg?: boolean;
  headerOffset?: number;
};

export function HeaderMenu({
  navigation,
  primaryAction,
  secondaryAction,
  open,
  onOpenChange,
  logoSrc,
  logoAlt = SITE_NAME,
  isLogoSvg = false,
  headerOffset = 0,
}: HeaderMenuProps) {
  const pathname = usePathname();
  const groups = useMemo(() => sanitizeNavigationGroups(navigation?.groups ?? []), [navigation]);
  const sanitizedPrimary = useMemo(() => sanitizeNavAction(primaryAction), [primaryAction]);
  const sanitizedSecondary = useMemo(() => sanitizeNavAction(secondaryAction), [secondaryAction]);

  const contextAwareGroupKey = useMemo(() => {
    if (!pathname) return null;

    for (const group of groups) {
      const firstPrimaryItem = group.primaryItems?.[0];
      if (!firstPrimaryItem) continue;

      const firstSecondaryItem = firstPrimaryItem.secondaryItems?.[0];
      if (!firstSecondaryItem) continue;

      const href = resolveCtaHref(firstSecondaryItem.href);
      if (!href) continue;

      const normalizedHref = href.split("#")[0];
      const normalizedPathname = pathname.split("#")[0];

      if (normalizedHref === normalizedPathname) {
        return group.key;
      }
    }

    return null;
  }, [groups, pathname]);

  const defaultGroupKey =
    contextAwareGroupKey ??
    groups.find((group) => group.defaultActive)?.key ??
    groups[0]?.key ??
    null;
  const hasPrimaryAction = Boolean(
    sanitizedPrimary?.label && resolveCtaHref(sanitizedPrimary?.href),
  );
  const hasSecondaryAction = Boolean(
    sanitizedSecondary?.label && resolveCtaHref(sanitizedSecondary?.href),
  );

  const [activeGroupKey, setActiveGroupKey] = useState<string | null>(defaultGroupKey);
  const [hoveredGroupKey, setHoveredGroupKey] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      startTransition(() => {
        setActiveGroupKey(defaultGroupKey);
      });
    } else {
      startTransition(() => {
        setHoveredGroupKey(null);
      });
    }
  }, [defaultGroupKey, open]);

  useEffect(() => {
    if (open) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "unset";
    }
    return () => {
      document.body.style.overflow = "unset";
    };
  }, [open]);

  useEffect(() => {
    if (!open) {
      return;
    }
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        onOpenChange(false);
      }
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [open, onOpenChange]);

  const displayKey = hoveredGroupKey ?? activeGroupKey;
  const displayGroup =
    groups.find((group) => group.key === displayKey) ?? groups.find((group) => group.key) ?? null;

  if (groups.length === 0) {
    return null;
  }

  return (
    <AnimatePresence initial={false} mode="popLayout">
      {open && (
        <motion.div
          key="menu-root"
          className="fixed inset-0 z-[40]" // parent wrapper - below header z-50
          initial="closed"
          animate="open"
          exit="closed"
          variants={parentVariants}
        >
          {/* Overlay (render first so it animates first on open, last on close) */}
          <motion.div
            key="header-menu-mask"
            className={cn("absolute inset-0 bg-black/40")}
            style={{
              top: `${headerOffset}px`,
            }}
            onClick={() => onOpenChange(false)}
            variants={overlayVariants}
          />

          {/* Panel */}
          <motion.div
            key="header-menu-panel"
            className={cn(
              "desktop:bg-black/70 fixed left-0 z-[41] w-full overflow-y-auto bg-black",
            )}
            style={{
              top: `${headerOffset}px`,
              height: `calc(100vh - ${headerOffset}px)`,
            }}
            onClick={(event) => {
              const target = event.target as HTMLElement;
              const isInteractive = target.closest(
                'button, a, input, select, textarea, [role="tab"], [role="button"]',
              );
              if (!isInteractive) onOpenChange(false);
            }}
            variants={panelVariants}
          >
            <Container className="relative h-full py-12">
              <button
                onClick={() => onOpenChange(false)}
                className="text-foreground hover:text-emerald-light right-padding-global p-padding-element-3xs desktop:block absolute top-2.5 z-10 cursor-pointer transition-colors"
                aria-label="Close menu"
              >
                <CloseIcon className="size-11" />
              </button>

              {/* Mobile Menu */}
              <div className="desktop:hidden flex h-full flex-col overflow-y-auto">
                {/* Header with logo and icons */}
                <div className="px-padding-global py-padding-element-md flex items-center justify-between border-b border-neutral-800">
                  {logoSrc ? (
                    <Image
                      src={logoSrc}
                      alt={logoAlt}
                      width={200}
                      height={52}
                      className="h-9 w-auto"
                      priority
                      unoptimized={isLogoSvg}
                    />
                  ) : null}
                  <div className="gap-gap-element-lg flex items-center">
                    {/* <button
                        className="text-foreground transition-colors hover:text-neutral-300"
                        aria-label="Search"
                      >
                        <icons.Search className="h-5 w-5" />
                      </button> */}
                    <button
                      className="text-foreground p-padding-element-3xs transition-colors hover:text-neutral-300"
                      aria-label="Profile"
                    >
                      <icons.Account className="size-8" />
                    </button>
                    <button
                      onClick={() => onOpenChange(false)}
                      className="text-foreground p-padding-element-3xs transition-colors hover:text-neutral-300"
                      aria-label="Close menu"
                    >
                      <CloseIcon className="size-8" />
                    </button>
                  </div>
                </div>

                {/* Navigation content */}
                <div className="flex flex-1 flex-col overflow-y-auto">
                  <div className="flex flex-col px-4 pt-6">
                    <MobileNavAccordion
                      groups={groups.map((group) => ({
                        key: group.key,
                        label: group.label,
                        links: buildMobileLinks(group),
                        group: group,
                      }))}
                      variant="overlay"
                      onNavigate={() => onOpenChange(false)}
                      defaultOpenKey={null}
                    />

                    {/* Action buttons */}
                    {hasPrimaryAction || hasSecondaryAction ? (
                      <div className="mt-6 flex flex-col gap-3 border-t border-neutral-800 pt-6 pb-8">
                        <HeaderActionButton
                          action={sanitizedPrimary}
                          fallbackVariant="primary"
                          className="w-full rounded-lg bg-white text-black hover:bg-neutral-100"
                          onClick={() => onOpenChange(false)}
                        />
                        <HeaderActionButton
                          action={sanitizedSecondary}
                          fallbackVariant="secondary"
                          className="w-full rounded-lg border-2 border-white bg-transparent text-white hover:bg-white/10"
                          onClick={() => onOpenChange(false)}
                        />
                      </div>
                    ) : null}
                  </div>
                </div>
              </div>

              {/* Desktop: 3-column layout */}
              <div
                className="desktop:grid desktop:gap-6 hidden h-full"
                style={{ gridTemplateColumns: "1fr 2fr 1fr" }}
              >
                {/* Desktop: Tabs Column */}
                <div className="flex flex-col gap-8 pt-8">
                  {groups.map((group, index) => {
                    const isActive = (hoveredGroupKey ?? activeGroupKey) === group.key;
                    return (
                      <button
                        key={group.key}
                        data-sanity-edit-target=""
                        onClick={() => setActiveGroupKey(group.key)}
                        onMouseEnter={() => setActiveGroupKey(group.key)}
                        className={`text-h4 text-left font-normal transition-colors ${
                          isActive ? "text-foreground" : "hover:text-foreground text-neutral-300"
                        }`}
                      >
                        <TextAnimate animation="slideInUp" by="character" delay={0.05 * index}>
                          {group.label}
                        </TextAnimate>
                      </button>
                    );
                  })}
                </div>

                {/* Desktop: Columns 2 & 3 wrapper */}
                <div className="col-span-2 grid gap-6" style={{ gridTemplateColumns: "2fr 1fr" }}>
                  {/* Desktop: Columns 2 */}
                  <motion.div
                    className="desktop:pt-8 desktop:pr-12 desktop:pl-10 relative flex flex-col px-4 pt-4"
                    initial={{ opacity: 0, x: -30 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.3, delay: 0.2, ease: "easeInOut" }}
                  >
                    {/* Desktop: Columns 2 - Divider */}
                    <motion.div
                      className="desktop:block absolute top-0 left-0 hidden w-px bg-neutral-700"
                      initial={{ height: 0 }}
                      animate={{ height: "100%" }}
                      transition={{ duration: 0.6, delay: 0.5, ease: "easeInOut" }}
                    />
                    <AnimatePresence mode="wait">
                      <motion.div
                        key={displayGroup?.key ?? "empty"}
                        initial={{ opacity: 0, x: -30 }}
                        animate={{ opacity: 1, x: 0 }}
                        exit={{ opacity: 0, x: 50 }}
                        transition={{ duration: 0.3, ease: "easeInOut" }}
                      >
                        <DesktopPrimaryColumn
                          primaryItems={displayGroup?.primaryItems ?? []}
                          onNavigate={() => onOpenChange(false)}
                        />
                      </motion.div>
                    </AnimatePresence>
                  </motion.div>

                  <motion.div
                    className="desktop:max-h-[calc(100vh-72px-96px)] desktop:overflow-auto desktop:pt-8 desktop:pr-6 desktop:[scrollbar-color:grey_transparent] px-4 pt-4"
                    initial={{ opacity: 0, x: -30 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.3, delay: 0.3, ease: "easeInOut" }}
                  >
                    <AnimatePresence mode="wait">
                      <motion.div
                        key={`${displayGroup?.key ?? "empty"}-aside`}
                        initial={{ opacity: 0, x: -30 }}
                        animate={{ opacity: 1, x: 0 }}
                        exit={{ opacity: 0, x: 50 }}
                        transition={{ duration: 0.3, ease: "easeInOut" }}
                      >
                        <DesktopAside group={displayGroup} onNavigate={() => onOpenChange(false)} />
                      </motion.div>
                    </AnimatePresence>
                  </motion.div>
                </div>
              </div>
            </Container>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

type HeaderMobileSheetContentProps = {
  navigation: HeaderNavigation | null;
  primaryAction?: HeaderNavAction | null;
  secondaryAction?: HeaderNavAction | null;
  onNavigate?: () => void;
  logoSrc?: string | null;
  logoAlt?: string;
  isLogoSvg?: boolean;
};

export function HeaderMobileSheetContent({
  navigation,
  primaryAction,
  secondaryAction,
  onNavigate,
  logoSrc,
  logoAlt = SITE_NAME,
  isLogoSvg = false,
}: HeaderMobileSheetContentProps) {
  const groups = useMemo(() => sanitizeNavigationGroups(navigation?.groups ?? []), [navigation]);
  const sanitizedPrimary = useMemo(() => sanitizeNavAction(primaryAction), [primaryAction]);
  const sanitizedSecondary = useMemo(() => sanitizeNavAction(secondaryAction), [secondaryAction]);
  const hasPrimaryAction = Boolean(
    sanitizedPrimary?.label && resolveCtaHref(sanitizedPrimary?.href),
  );
  const hasSecondaryAction = Boolean(
    sanitizedSecondary?.label && resolveCtaHref(sanitizedSecondary?.href),
  );

  if (groups.length === 0) {
    return null;
  }

  return (
    <div className="flex h-full flex-col bg-black">
      {/* Header with logo and icons */}
      <div className="px-padding-global py-padding-element-md flex items-center justify-between border-b border-neutral-800">
        {logoSrc ? (
          <Link href="/" onClick={onNavigate}>
            <Image
              src={logoSrc}
              alt={logoAlt}
              className="h-9 w-auto"
              width={200}
              height={52}
              priority
              unoptimized={isLogoSvg}
            />
          </Link>
        ) : null}
        <div className="flex items-center gap-4">
          {/* <button
            className="text-foreground transition-colors hover:text-neutral-300"
            aria-label="Search"
          >
            <SearchIcon className="h-5 w-5" />
          </button> */}
          <button
            className="text-foreground p-padding-element-3xs px-padd transition-colors hover:text-neutral-300"
            aria-label="Profile"
          >
            <icons.Account className="size-8" />
          </button>
          <button
            onClick={onNavigate}
            className="text-foreground p-padding-element-3xs transition-colors hover:text-neutral-300"
            aria-label="Close menu"
          >
            <CloseIcon className="size-8" />
          </button>
        </div>
      </div>

      {/* Navigation content */}
      <div className="flex flex-1 flex-col overflow-y-auto">
        <div className="flex flex-col px-4 pt-6">
          <MobileNavAccordion
            groups={groups.map((group) => ({
              key: group.key,
              label: group.label,
              links: buildMobileLinks(group),
              group: group,
            }))}
            variant="sheet"
            onNavigate={onNavigate}
            defaultOpenKey={null}
          />

          {/* Action buttons */}
          {hasPrimaryAction || hasSecondaryAction ? (
            <div className="mt-6 flex flex-col gap-3 border-t border-neutral-800 pt-6 pb-8">
              <HeaderActionButton
                action={sanitizedPrimary}
                fallbackVariant="primary"
                className="w-full rounded-lg bg-white text-black hover:bg-neutral-100"
                onClick={onNavigate}
              />
              <HeaderActionButton
                action={sanitizedSecondary}
                fallbackVariant="secondary"
                className="w-full rounded-lg border-2 border-white bg-transparent text-white hover:bg-white/10"
                onClick={onNavigate}
              />
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
}

function sanitizeNavigationGroups(groups: HeaderNavGroup[]): SanitizedNavGroup[] {
  return groups.map((group) => ({
    ...group,
    key: cleanRequiredString(group.key),
    primaryItems: group.primaryItems.map((primaryItem) => ({
      ...primaryItem,

      materialIcon: cleanOptionalString(primaryItem.materialIcon),
      secondaryItems: primaryItem.secondaryItems.map((secondaryItem) => ({
        ...secondaryItem,
        title: cleanRequiredString(secondaryItem.title),
        subtitle: cleanOptionalString(secondaryItem.subtitle),
        href: secondaryItem.href,
      })),
    })),
    aside: sanitizeNavAside(group.aside),
  }));
}

function sanitizeNavCard(card: HeaderNavCard, _key: string): SanitizedNavCard {
  return {
    ...card,
    _key,
    href: card.href, // Keep hrefObject as-is, will be resolved in components
    imageUrl: cleanOptionalString(card.imageUrl),
    image: card.image
      ? {
          ...card.image,
          asset: card.image?.asset,
        }
      : undefined,
  };
}

function sanitizeNavAside(aside: HeaderNavGroup["aside"]): SanitizedNavAside | null {
  if (!aside) {
    return null;
  }

  const rawAside = aside as RawNavAside;
  const autoPopulate = !!rawAside.autoPopulateLatest;

  let resolvedCards: SanitizedNavCard[] = [];
  if (autoPopulate) {
    const latest = rawAside.latestArticles ?? [];
    resolvedCards = latest.slice(0, 2).map((article, i) => {
      const synthetic = {
        contentType: "article",
        articleReference: article,
      } as unknown as HeaderNavCard;
      return sanitizeNavCard(synthetic, `auto-${i}`);
    });
  } else {
    resolvedCards = (aside.cards ?? []).map((card, index) =>
      sanitizeNavCard(
        card,
        ("_key" in card && typeof card._key === "string" ? card._key : null) ?? `card-${index}`,
      ),
    );
  }

  return {
    variant: cleanOptionalString(aside.variant),
    cards: resolvedCards,
    linkList: aside.linkList
      ? {
          title: aside.linkList.title,
          links: (aside.linkList.links ?? []).map((link, index) => ({
            _key:
              ("_key" in link && typeof link._key === "string" ? link._key : null) ??
              `link-${index}`,
            title: cleanRequiredString(link.title),
            subtitle: cleanOptionalString(link.subtitle),
            href: resolveCtaHref(link.href) ?? undefined,
          })),
        }
      : null,
  };
}

function sanitizeNavAction(action?: HeaderNavAction | null): HeaderNavAction | null {
  if (!action) {
    return action ?? null;
  }

  return {
    ...action,
    href: action.href, // Keep hrefObject as-is, will be resolved in components
    variant: cleanOptionalString(action.variant),
  };
}

function cleanRequiredString(value: string): string {
  return stripStega(value);
}

function cleanOptionalString<T extends string | null | undefined>(value: T): T {
  if (typeof value !== "string") {
    return value;
  }
  return stripStega(value) as T;
}

function buildMobileLinks(group: SanitizedNavGroup): Array<{
  _key: string;
  title: string;
  subtitle?: string;
  href?: string;
}> {
  const links: Array<{
    _key: string;
    title: string;
    subtitle?: string;
    href?: string;
  }> = [];

  for (const primaryItem of group.primaryItems) {
    for (const secondaryItem of primaryItem.secondaryItems) {
      links.push({
        _key: secondaryItem._key,
        title: secondaryItem.title,
        subtitle: secondaryItem.subtitle,
        href: resolveCtaHref(secondaryItem.href) ?? undefined,
      });
    }
  }

  return links;
}

type MobileGroup = {
  key: string;
  label: string;
  links: Array<{
    _key: string;
    title: string;
    subtitle?: string;
    href?: string;
  }>;
  group?: SanitizedNavGroup;
};

type MobileNavAccordionProps = {
  groups: MobileGroup[];
  variant: "overlay" | "sheet";
  onNavigate?: () => void;
  defaultOpenKey?: string | null;
};

function MobileNavAccordion({
  groups,
  variant,
  onNavigate,
  defaultOpenKey,
}: MobileNavAccordionProps) {
  const PlusIcon = icons.Plus;
  const MinusIcon = icons.Minus;
  const [openItem, setOpenItem] = useState<string | null>(defaultOpenKey ?? null);

  useEffect(() => {
    if (defaultOpenKey !== undefined) {
      startTransition(() => {
        setOpenItem(defaultOpenKey);
      });
    }
  }, [defaultOpenKey]);

  const handleValueChange = (value: string | undefined) => {
    if (value && value !== openItem) {
      const navPanel = document
        .querySelector('[data-state="open"]')
        ?.closest(".overflow-y-auto") as HTMLElement;

      const scrollContainer =
        navPanel ||
        document.querySelector(".overflow-y-auto") ||
        document.scrollingElement ||
        document.documentElement;

      requestAnimationFrame(() => {
        if (scrollContainer && scrollContainer.scrollTo) {
          scrollContainer.scrollTo({ top: 0, behavior: "smooth" });
        }
      });

      setOpenItem(value);
    } else {
      setOpenItem(value ?? null);
    }
  };

  return (
    <Accordion
      type="single"
      collapsible
      className="w-full"
      value={openItem ?? undefined}
      onValueChange={handleValueChange}
    >
      {groups.map((group) => {
        const isOpen = openItem === group.key;

        return (
          <AccordionItem
            key={group.key}
            value={group.key}
            className={variant === "sheet" ? "border-neutral-800" : "border-neutral-dark"}
          >
            <AccordionTrigger className="hover:no-underline [&>svg]:hidden">
              <div className="flex w-full items-center justify-between">
                <span
                  className={
                    variant === "sheet"
                      ? "text-foreground text-left font-normal"
                      : "text-h4 text-foreground text-left font-normal"
                  }
                >
                  {group.label}
                </span>
                {isOpen ? (
                  <MinusIcon className="h-5 w-5 shrink-0" />
                ) : (
                  <PlusIcon className="h-5 w-5 shrink-0" />
                )}
              </div>
            </AccordionTrigger>
            <AccordionContent>
              <div className="flex flex-col gap-8 pt-2">
                {group.group?.primaryItems?.map((primaryItem, primaryIndex) => {
                  const isSimpleView = primaryItem.simpleView ?? false;
                  const shouldShowSeparator =
                    primaryIndex < (group.group?.primaryItems?.length || 0) - 1 && !isSimpleView;

                  return (
                    <div key={primaryItem._key}>
                      <div className="flex flex-col gap-3">
                        {primaryItem.secondaryItems.map((secondaryItem, index) => {
                          const href = resolveCtaHref(secondaryItem.href) || "#";
                          const isExternal = /^https?:\/\//i.test(href);
                          const Icon = primaryItem.materialIcon
                            ? icon(primaryItem.materialIcon)
                            : null;
                          return (
                            <a
                              key={secondaryItem._key}
                              href={href}
                              target={isExternal ? "_blank" : undefined}
                              rel={isExternal ? "noopener noreferrer" : undefined}
                              onClick={onNavigate}
                              className="group gap-gap-element-lg p-padding-element-sm flex w-full flex-1 flex-row rounded-lg transition-colors hover:bg-[soft-light,normal] hover:bg-[linear-gradient(180deg,rgba(189,212,252,0.20)_0%,rgba(178,178,179,0.20)_100%)]"
                            >
                              <div className="mb-2 flex gap-3">
                                <div
                                  className={cn(
                                    "flex h-11 w-11 items-center justify-center rounded-lg p-0.5",
                                    index === 0 && primaryItem.materialIcon
                                      ? "bg-emerald-darkest group-hover:bg-text-active-alternate group-hover:text-text-dark"
                                      : "transparent",
                                  )}
                                >
                                  {Icon && index === 0 && <Icon className="size-10" />}
                                </div>
                              </div>
                              <div>
                                <span
                                  className={`group-hover:text-emerald-light ${index === 0 ? "typography-text-large-normal" : "typography-text-medium-normal"} text-white transition-colors`}
                                >
                                  {secondaryItem.title}
                                </span>
                                {secondaryItem.subtitle && (
                                  <p className="text-sm text-neutral-400 transition-colors group-hover:text-neutral-300">
                                    {secondaryItem.subtitle}
                                  </p>
                                )}
                              </div>
                            </a>
                          );
                        })}
                      </div>
                      {shouldShowSeparator && <Separator className="my-2 bg-white/20" />}
                    </div>
                  );
                }) || (
                  <p className="pt-4 text-sm text-neutral-400">No navigation items available.</p>
                )}
                {group.group?.aside && isOpen ? (
                  <MobileAside aside={group.group.aside} onNavigate={onNavigate} />
                ) : null}
              </div>
            </AccordionContent>
          </AccordionItem>
        );
      })}
    </Accordion>
  );
}

function MobileAside({
  aside,
  onNavigate,
}: {
  aside: SanitizedNavAside | null;
  onNavigate?: () => void;
}) {
  if (!aside || aside.variant === "none") {
    return null;
  }

  if (aside.variant === "list" && aside.linkList) {
    const links = aside.linkList.links ?? [];
    return (
      <div className="flex flex-col gap-6">
        <h3 className="text-large font-normal text-neutral-400">{aside.linkList.title}</h3>
        <div className="flex flex-col gap-4">
          {links.map((link) => {
            const href = link.href || "#";
            const isExternal = /^https?:\/\//.test(href);
            return (
              <a
                key={link._key}
                href={href}
                target={isExternal ? "_blank" : undefined}
                rel={isExternal ? "noreferrer noopener" : undefined}
                className="text-regular text-foreground hover:text-emerald-light transition-colors"
                onClick={onNavigate}
              >
                {link.title}
              </a>
            );
          })}
        </div>
      </div>
    );
  }

  if (aside.variant === "cards" && aside.cards && aside.cards.length > 0) {
    return (
      <div className="flex flex-col gap-8">
        {aside.cards.map((card) => (
          <AsideCard key={card._key} card={card} onNavigate={onNavigate} />
        ))}
      </div>
    );
  }

  return null;
}

function DesktopPrimaryColumn({
  primaryItems,
  onNavigate,
}: {
  primaryItems: SanitizedNavGroup["primaryItems"];
  onNavigate?: () => void;
}) {
  if (!primaryItems || primaryItems.length === 0) {
    return (
      <p className="pt-10 text-sm text-neutral-400">
        Navigation group is empty. Add links in Sanity to populate this area.
      </p>
    );
  }

  return (
    <div className="flex flex-col gap-8">
      {primaryItems.map((primaryItem, primaryIndex) => {
        const isSimpleView = primaryItem.simpleView ?? false;
        const shouldShowSeparator = primaryIndex < primaryItems.length - 1 && !isSimpleView;

        return (
          <div key={primaryItem._key}>
            <div className="flex flex-row gap-4">
              <div className="grid w-full gap-3">
                {primaryItem.secondaryItems.map((secondaryItem, index) => {
                  const href = resolveCtaHref(secondaryItem.href) || "#";
                  const isExternal = /^https?:\/\//i.test(href);
                  const Icon = primaryItem.materialIcon ? icon(primaryItem.materialIcon) : null;
                  return (
                    <a
                      key={secondaryItem._key}
                      href={href}
                      target={isExternal ? "_blank" : undefined}
                      rel={isExternal ? "noopener noreferrer" : undefined}
                      onClick={onNavigate}
                      className="group gap-gap-element-lg p-padding-element-sm flex flex-row rounded-lg transition-colors hover:bg-[soft-light,normal] hover:bg-[linear-gradient(180deg,rgba(189,212,252,0.20)_0%,rgba(178,178,179,0.20)_100%)]"
                    >
                      <div className="mb-2 flex gap-3">
                        <div
                          className={cn(
                            "flex h-11 w-11 items-center justify-center rounded-lg p-0.5",
                            index === 0 && primaryItem.materialIcon
                              ? "bg-emerald-darkest group-hover:bg-text-active-alternate group-hover:text-text-dark"
                              : "transparent",
                          )}
                        >
                          {Icon && index === 0 && <Icon className="size-10" />}
                        </div>
                      </div>
                      <div className="pt-1">
                        <span
                          className={`group-hover:text-emerald-light ${index === 0 ? "typography-text-large-normal" : "typography-text-medium-normal"} text-white transition-colors`}
                        >
                          {secondaryItem.title}
                        </span>
                        {secondaryItem.subtitle && (
                          <p className="text-sm text-neutral-400 transition-colors group-hover:text-neutral-300">
                            {secondaryItem.subtitle}
                          </p>
                        )}
                      </div>
                    </a>
                  );
                })}
              </div>
            </div>
            {shouldShowSeparator && <Separator className="my-2 bg-white/20" />}
          </div>
        );
      })}
    </div>
  );
}

function DesktopAside({
  group,
  onNavigate,
}: {
  group: SanitizedNavGroup | null;
  onNavigate?: () => void;
}) {
  if (!group) {
    return null;
  }
  const aside = group.aside ?? null;
  if (!aside || aside.variant === "none") {
    return null;
  }

  if (aside.variant === "list" && aside.linkList) {
    const links = aside.linkList.links ?? [];
    return (
      <div className="flex flex-col gap-6">
        <h3 className="text-large font-normal text-neutral-400">{aside.linkList.title}</h3>
        <div className="flex flex-col gap-4">
          {links.map((link) => {
            const href = link.href || "#";
            const isExternal = /^https?:\/\//.test(href);
            return (
              <a
                key={link._key}
                href={href}
                target={isExternal ? "_blank" : undefined}
                rel={isExternal ? "noreferrer noopener" : undefined}
                className="text-regular text-foreground hover:text-emerald-light transition-colors"
                onClick={onNavigate}
              >
                {link.title}
              </a>
            );
          })}
        </div>
      </div>
    );
  }

  if (aside.variant === "cards" && aside.cards && aside.cards.length > 0) {
    return (
      <div className="flex flex-col gap-8">
        {aside.cards.map((card) => (
          <AsideCard key={card._key} card={card} onNavigate={onNavigate} />
        ))}
      </div>
    );
  }

  return null;
}

const ArrowForward = icons.ArrowForward;

function AsideCard({ card, onNavigate }: { card: SanitizedNavCard; onNavigate?: () => void }) {
  let href = resolveCtaHref(card.href) || "#";
  let isExternal = /^https?:\/\//.test(href);
  let ctaLabel = card.ctaLabel || "Read More";
  let cardImage =
    (card.image?.asset && "url" in card.image.asset && typeof card.image.asset.url === "string"
      ? card.image.asset.url
      : null) ?? (typeof card.imageUrl === "string" ? card.imageUrl : null);

  let body: string | PortableTextValue = "";

  let title = card.title;
  let imageAlt = card.image?.alt || card.title;
  let badge = card.badge || null;
  let publishedAt: string | null = null;

  const contentType = stegaClean(card.contentType);
  const isArticleCard = contentType === "article" && !!card.articleReference;

  if (isArticleCard && card.articleReference) {
    const content = resolveReference(card.articleReference);
    cardImage = urlForImageCached(content.image?.image)?.width(304).url() || null;
    title = content.title;
    ctaLabel = "Read More";
    href = getArticleUrl(content);
    isExternal = false;
    imageAlt = content.image?.alt || content.title;
    publishedAt = content.publishedAt || null;
    if (!badge && content._type) {
      badge = capitalizeFirst(content._type);
    }
  }

  if (contentType === "offering" && card.offeringReference) {
    const content = resolveReference(card.offeringReference);
    cardImage = urlForImageCached(content.image?.image)?.width(304).url() || null;
    title = content.title;
    ctaLabel = "";
    href = resolveCtaHref(content.href) || "#";
    isExternal = /^https?:\/\//.test(href);
    body = content.body || "";
    imageAlt = content.image?.alt || content.title;
  }

  return (
    <a
      href={href}
      target={isExternal ? "_blank" : undefined}
      rel={isExternal ? "noreferrer noopener" : undefined}
      className={`group px-padding-element-md py-padding-element-lg gap-gap-element-lg relative flex shrink-0 cursor-pointer flex-col overflow-hidden rounded-2xl from-neutral-800 to-neutral-900 transition-all duration-300 ${!cardImage ? "border-indigo-lighter/30 border" : ""}`}
      onClick={onNavigate}
    >
      <div className="pointer-events-none absolute inset-0 flex flex-col rounded-2xl bg-white/0 transition-colors group-hover:bg-[soft-light,normal] group-hover:bg-[linear-gradient(180deg,rgba(189,212,252,0.20)_0%,rgba(178,178,179,0.20)_100%)]" />
      {cardImage ? (
        <div className="relative z-10 flex items-start overflow-hidden">
          {badge ? (
            <span
              className={
                isArticleCard
                  ? "absolute top-2 left-2 z-20 inline-block rounded-full border-[1.5px] border-white bg-transparent px-3 py-0.5 text-xs font-normal text-white"
                  : "bg-emerald-light/20 text-emerald-light absolute top-2 left-2 z-20 inline-block rounded px-2 py-0.5 text-xs font-medium"
              }
            >
              {badge}
            </span>
          ) : null}
          <Image
            src={cardImage}
            alt={imageAlt || ""}
            width={304}
            height={304}
            className="w-full rounded-lg object-cover object-center"
            sizes="(min-width: 1024px) 320px, 100vw"
            unoptimized={
              !(
                card.image?.asset &&
                "url" in card.image.asset &&
                typeof card.image.asset.url === "string" &&
                card.image.asset.url
              )
            }
          />
        </div>
      ) : null}
      <div className="gap-gap-element-sm flex flex-col items-start">
        <Button variant="link" className="max-w-full items-start text-left whitespace-normal">
          <span className="line-clamp-3">{title}</span> <ArrowForward className="mt-1 shrink-0" />
        </Button>
        {isArticleCard && publishedAt ? (
          <time
            dateTime={publishedAt}
            className="text-neutral-lighter typography-text-regular-normal relative z-10"
          >
            {formatDate(publishedAt)}
          </time>
        ) : (
          ctaLabel && (
            <span className="text-neutral-lighter typography-text-regular-normal relative z-10 transition-colors">
              {ctaLabel}
            </span>
          )
        )}
        {body && (
          <PortableText
            value={body as PortableTextValue}
            className="**:text-neutral-lighter line-clamp-5"
          />
        )}
      </div>
    </a>
  );
}
